<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import {
  isWhatsAppComplete,
  getAllowedFileTypesByChannel,
} from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useFileUpload } from 'dashboard/composables/useFileUpload';
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  mediaTypeForFile,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
  renderTemplatePreview,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  // Enables pasting/uploading a local file for the media header. Only set from
  // contexts that have a live conversation to attach the file to.
  conversationId: {
    type: [Number, String],
    default: undefined,
  },
  // A file captured elsewhere (e.g. pasted while the reply box was disabled)
  // that should be auto-attached as soon as this template's media step mounts.
  pendingMediaFile: {
    type: File,
    default: null,
  },
  sendRenderedContent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
// Holds { file, blob } — `blob` only exists when direct uploads are enabled.
const attachedMedia = ref(null);
const mediaInputRef = ref(null);

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const headerText = computed(() => {
  return headerComponent.value?.format === 'TEXT'
    ? headerComponent.value?.text || ''
    : '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasBodyVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const hasTextHeaderVariables = computed(() => {
  return headerText.value?.match(/{{([^}]+)}}/g) !== null;
});

const hasVariables = computed(
  () => hasBodyVariables.value || hasTextHeaderVariables.value
);

const renderedHeader = computed(() => {
  return renderTemplatePreview(
    headerText.value,
    processedParams.value.header || {}
  );
});

const renderedTemplate = computed(() => {
  return renderTemplatePreview(
    bodyText.value,
    processedParams.value.body || {}
  );
});

// Completeness validation is shared with the mobile app via @chatwoot/utils.
const isFormInvalid = computed(
  () => !isWhatsAppComplete(props.template, processedParams.value)
);

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

// The header format dictates what can be attached, so narrow the channel-wide
// list down to it (e.g. an IMAGE header only accepts images).
const mediaAcceptTypes = computed(() => {
  const allowed = getAllowedFileTypesByChannel({
    channelType: INBOX_TYPES.WHATSAPP,
  })
    .split(',')
    .map(type => type.trim());
  const format = formatType.value.toLowerCase();

  if (format === 'image' || format === 'video') {
    return allowed.filter(type => type.startsWith(`${format}/`)).join(', ');
  }
  return allowed
    .filter(type => !/^(image|video|audio)\//.test(type))
    .join(', ');
});

// Delegates to the shared composable so we honour DIRECT_UPLOADS_ENABLED: when it
// is off (the default) the raw file is sent as multipart instead of pre-uploaded.
const { onFileUpload } = useFileUpload({
  inbox: { channel_type: INBOX_TYPES.WHATSAPP },
  attachFile: ({ file, blob }) => {
    attachedMedia.value = { file: file.file, blob };
    processedParams.value.header ??= {};
    // Local preview only — the backend swaps in the real URL at send time.
    processedParams.value.header.media_url = URL.createObjectURL(file.file);
    if (isDocumentTemplate.value && !processedParams.value.header.media_name) {
      processedParams.value.header.media_name = file.file.name;
    }
  },
});

const uploadMediaFile = file => {
  if (!file || !props.conversationId) return;

  if (
    !isFileTypeAllowedForChannel(file, { channelType: INBOX_TYPES.WHATSAPP })
  ) {
    useAlert(
      t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', { fileName: file.name })
    );
    return;
  }

  // Paste and drag-and-drop bypass the file picker's accept list, so the header
  // format has to be enforced here too — WhatsApp rejects a mismatch.
  if (mediaTypeForFile(file) !== formatType.value.toLowerCase()) {
    useAlert(
      t('WHATSAPP_TEMPLATES.PARSER.MEDIA_TYPE_MISMATCH', {
        type: formatType.value,
      })
    );
    return;
  }

  const { name, type, size } = file;
  onFileUpload({ name, type, size, file });
};

const clearAttachedMedia = () => {
  attachedMedia.value = null;
  if (processedParams.value.header) {
    processedParams.value.header.media_url = '';
  }
};

const onMediaFileSelected = e => {
  const [file] = e.target.files;
  uploadMediaFile(file);
  e.target.value = '';
};

const onMediaDrop = e => {
  const [file] = e.dataTransfer.files;
  uploadMediaFile(file);
};

const onMediaPaste = e => {
  const [file] = Array.from(e.clipboardData?.files || []);
  if (!file) return;
  e.preventDefault();
  uploadMediaFile(file);
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;
  const media = attachedMedia.value;

  const payload = {
    message: props.sendRenderedContent
      ? renderedTemplate.value
      : bodyText.value,
    pendingMessageContent: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      content_mode: props.sendRenderedContent ? 'rendered' : 'raw_template',
      processed_params: processedParams.value,
    },
    // Mirrors ReplyBox: a blob signed id when direct uploads are on, the raw file otherwise.
    files: media ? [media.blob?.signed_id ?? media.file] : undefined,
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(() => {
  initializeTemplateParameters();
  if (props.pendingMediaFile) uploadMediaFile(props.pendingMediaFile);
});

onUnmounted(() => {
  if (processedParams.value.header?.media_url?.startsWith('blob:')) {
    URL.revokeObjectURL(processedParams.value.header.media_url);
  }
});

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedHeader,
  renderedTemplate,
  isFormInvalid,
  v$,
  attachedMedia,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div
            v-if="renderedHeader"
            class="mb-2 text-sm font-medium whitespace-pre-wrap text-n-slate-12"
          >
            {{ renderedHeader }}
          </div>
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div
          v-if="conversationId"
          tabindex="0"
          class="flex flex-col gap-2 items-center justify-center p-4 mb-2.5 text-center rounded-lg border border-dashed cursor-pointer border-n-weak hover:border-n-slate-6 focus:outline-none focus:border-n-brand"
          @click="mediaInputRef?.click()"
          @paste.stop="onMediaPaste"
          @dragover.prevent
          @drop.prevent="onMediaDrop"
        >
          <template v-if="attachedMedia">
            <img
              v-if="formatType.toLowerCase() === 'image'"
              :src="processedParams.header.media_url"
              class="object-cover rounded-md max-h-24"
            />
            <span class="max-w-full text-xs truncate text-n-slate-11">
              {{
                processedParams.header.media_name ||
                attachedMedia.file.name ||
                t('WHATSAPP_TEMPLATES.PARSER.MEDIA_ATTACHED')
              }}
            </span>
            <button
              type="button"
              class="text-xs text-n-blue-11 hover:underline"
              @click.stop="clearAttachedMedia"
            >
              {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_REMOVE') }}
            </button>
          </template>
          <span v-else class="text-xs text-n-slate-11">
            {{
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_LABEL', {
                type: formatType,
              })
            }}
          </span>
          <input
            ref="mediaInputRef"
            type="file"
            class="hidden"
            :accept="mediaAcceptTypes"
            @change="onMediaFileSelected"
          />
        </div>
        <div v-if="!attachedMedia" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Text Header Variables Section -->
      <div v-if="hasTextHeaderVariables && processedParams.header">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.HEADER_VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.header"
          :key="`header-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.header[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
